import React from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { FormTextarea } from '../components/shared/FormTextarea';
import { TagInput } from '../components/shared/TagInput';
import { FileUploader } from '../components/shared/FileUploader';
import { OnboardingData } from '../types';

interface Step7BrandAssetsProps {
  control: Control<OnboardingData>;
}

const Step7BrandAssets: React.FC<Step7BrandAssetsProps> = ({ control }) => {
  const { setValue, getValues } = useFormContext<OnboardingData>();

  const handleFileRemoved = (materialId: string) => {
    const currentDeletedIds = getValues('step7_brandAssets.deletedMaterialIds') || [];
    setValue('step7_brandAssets.deletedMaterialIds', [...currentDeletedIds, materialId]);
  };

  return (
    <div className="space-y-3">
      {/* Section 1: Upload Brand Assets */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-text">Upload Brand Assets</h3>

        <FileUploader
          name="step7_brandAssets.files"
          control={control}
          accept=".pdf,.doc,.txt,"
          maxSize={10 * 1024 * 1024} // 10MB
          multiple={true}
          onFileRemoved={handleFileRemoved}
        />
      </div>

      {/* Section 2: Brand Voice & Social URLs */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-text">Brand Voice & Social URLs</h3>
        <p className="text-xs">
          Tell the AI how your brand should sound when writing posts, newsletters, or emails.
        </p>

        <div className="border rounded-md p-5">
          {/* Brand tone */}
          <FormTextarea
            name="step7_brandAssets.tone"
            control={control}
            label="Describe your tone"
            placeholder="e.g., Professional but friendly, casual and fun, formal and authoritative..."
            rows={2}
            rules={{ required: 'This field is required' }}
          />

          <div className="grid grid-cols-2 gap-3 mt-4">
            {/* Words to avoid */}
            <div className="flex flex-col gap-2">
              <label className="block text-sm text-text font-medium">
                Words or phrases to avoid (optional)
              </label>
              <TagInput
                name="step7_brandAssets.wordsToAvoid"
                control={control}
                placeholder="Add words to avoid... (press Enter or comma)"
              />
            </div>

            {/* Key themes */}
            <div className="flex flex-col gap-2">
              <label className="block text-sm text-text font-medium">
                Key themes/values (optional)
              </label>
              <TagInput
                name="step7_brandAssets.keyThemes"
                control={control}
                placeholder="Add key themes... (press Enter or comma)"
              />
            </div>
          </div>

          {/* Social Examples */}
          <div className="mt-4">
            <label className="block text-sm text-text font-medium mb-1">
              Social Media Examples
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Add multiple examples of posts you like to help the AI understand your preferred style
              and tone.
            </p>

            <div className="space-y-4">
              {/* Twitter Examples */}
              <div className="flex flex-col gap-2">
                <label className="block text-sm text-text font-medium flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                  Twitter/X Examples (optional)
                </label>
                <TagInput
                  name="step7_brandAssets.socialExamples.twitterExamples"
                  control={control}
                  placeholder="Paste or type Twitter/X post examples... (press Enter to add)"
                  className="[&>div]:min-h-[120px] [&>div]:p-3 [&>div]:items-start [&>div]:content-start"
                  allowMultiline={true}
                />
              </div>

              {/* LinkedIn Examples */}
              <div className="flex flex-col gap-2">
                <label className="block text-sm text-text font-medium flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn Examples (optional)
                </label>
                <TagInput
                  name="step7_brandAssets.socialExamples.linkedinExamples"
                  control={control}
                  placeholder="Paste or type LinkedIn post examples... (press Enter to add)"
                  className="[&>div]:min-h-[120px] [&>div]:p-3 [&>div]:items-start [&>div]:content-start"
                  allowMultiline={true}
                />
              </div>

              {/* Blog URLs */}
              <div className="flex flex-col gap-2">
                <label className="block text-sm text-text font-medium flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                  Blog/Article URLs (optional)
                </label>
                <p className="text-xs text-gray-600 mb-1">
                  Add URLs to blogs or articles that represent your writing style. We'll analyze
                  them to match your tone.
                </p>
                <TagInput
                  name="step7_brandAssets.socialExamples.blogUrls"
                  control={control}
                  placeholder="Paste blog URLs... (press Enter to add)"
                  className="[&>div]:min-h-[80px] [&>div]:p-3 [&>div]:items-start [&>div]:content-start"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Step7BrandAssets };
